// src/hooks/useAdminReports.js

import { useState, useEffect, useRef, useCallback } from 'react';
import { ASSET_TYPES, ASSET_STATUS } from "../utils/assetUtils";

import { userService } from '../services/userService';
import { assetService } from '../services/assetService';
import { categoryService } from '../services/categoryService';
import { subCategoryService } from '../services/subCategoryService';
import { brandService } from '../services/brandService';
import { fetchAuditEvents } from '../services/auditService';
import { fetchAuditEventAssets } from '../services/auditHistoryService';
import { helpDeskService } from '../services/helpDeskService';
import { consumableSpareLogService } from '../services/consumableSpareLogService';

function resolveUserId(user) {
  if (!user) return null;
  return (
    user.uid ||
    user.userId ||
    user.id ||
    null
  );
}

const sessionReportCache = new Map();

export const clearReportCache = (userId = null) => {
  if (userId) sessionReportCache.delete(userId);
  else sessionReportCache.clear();
};

// ─────────────────────────────────────────────────────────────────────────────
// Utilities
// ─────────────────────────────────────────────────────────────────────────────

function num(val) {
  if (val === null || val === undefined || val === '') return 0;
  if (typeof val === 'number') return Number.isFinite(val) ? val : 0;
  const n = parseFloat(String(val).replace(/[^0-9.-]/g, ''));
  return Number.isFinite(n) ? n : 0;
}

function toDate(val) {
  if (!val) return null;
  if (typeof val?.toDate === 'function') return val.toDate();
  if (typeof val === 'object') {
    if (typeof val.seconds === 'number') return new Date(val.seconds * 1000);
    if (typeof val._seconds === 'number') return new Date(val._seconds * 1000);
  }
  const d = new Date(val);
  return Number.isNaN(d.getTime()) ? null : d;
}

function toDateStr(val) {
  const d = toDate(val);
  return d ? d.toLocaleDateString('en-IN') : 'N/A';
}

function daysUntil(targetDate) {
  if (!targetDate) return null;
  const now = new Date();
  const a = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  const b = Date.UTC(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate());
  return Math.ceil((b - a) / 86400000);
}

function key(v) {
  return v == null ? '' : String(v).trim().toLowerCase();
}

function isDeletedDoc(row) {
  if (!row) return true;
  if (row.isDeleted === true || row.deleted === true) return true;
  if (row.isActive === false) return true;
  const s = key(row.status);
  return s === 'deleted' || s === 'cancelled' || s === 'canceled';
}

function actualText(...candidates) {
  for (const c of candidates) {
    if (c == null) continue;
    const s = String(c).trim();
    if (!s) continue;
    const lower = s.toLowerCase();
    if (
      lower === 'system generated audit record' ||
      lower === 'data matched with db record' ||
      lower === 'system generated audit record.' ||
      lower === 'marked as missing in audit' ||
      lower === 'marked as missing/lost in system'
    ) continue;
    return s;
  }
  return '';
}

function resolveAuditStatus(h = {}, ev = {}) {
  return (
    h.status || h.audit_status || h.auditStatus ||
    h.verification_status || h.verificationStatus ||
    ev.status || ev.auditStatus || 'N/A'
  );
}

function assetKeys(row = {}) {
  return [row.id, row.assetId, row.asset_id, row.assetTag, row.asset_tag, row.assetCode]
    .map(key)
    .filter(Boolean);
}

function buildAssetIndex(assets = []) {
  const byKey = new Map();
  assets.forEach((a) => assetKeys(a).forEach((k) => byKey.set(k, a)));
  return byKey;
}

function findAsset(row, assetIndex) {
  for (const k of assetKeys(row)) {
    if (assetIndex.has(k)) return assetIndex.get(k);
  }
  return null;
}

function isConsumable(a) {
  if (!a) return false;
  const raw = a.assetType || a.type || '';
  const t = String(raw).trim().toLowerCase();
  if (!t) return false;

  const c = ASSET_TYPES?.CONSUMABLE ? String(ASSET_TYPES.CONSUMABLE).toLowerCase() : '';
  const s = ASSET_TYPES?.SPARE_PART ? String(ASSET_TYPES.SPARE_PART).toLowerCase() : '';

  if (c && t === c) return true;
  if (s && t === s) return true;
  return /consumable|spare/.test(t);
}

function buildConsumableStockMap(logs = [], consumptions = []) {
  const map = new Map();
  const ensure = (assetId) => {
    const k = String(assetId || '').trim();
    if (!k) return null;
    if (!map.has(k)) map.set(k, { purchased: 0, issued: 0, consumed: 0 });
    return map.get(k);
  };

  logs.forEach((log) => {
    if (!log) return;
    const row = ensure(log.assetId || log.asset_id);
    if (!row) return;
    const qty = num(log.qty ?? log.quantity);
    const type = String(log.transactionType || log.type || '').trim();
    if (type === 'Receipt') row.purchased += qty;
    else if (type === 'Issue') row.issued += qty;
  });

  consumptions.forEach((c) => {
    if (!c) return;
    const row = ensure(c.assetId || c.asset_id);
    if (!row) return;
    row.consumed += num(c.quantity ?? c.qty);
  });

  return map;
}

function historyEventId(h) {
  return String(h?.audit_event_id || h?.auditEventId || h?.eventId || '').trim();
}

// ─────────────────────────────────────────────────────────────────────────────
// Report builder
// ─────────────────────────────────────────────────────────────────────────────

function buildDetailedReports(
  assets = [],
  auditEvents = [],
  auditHistory = [],
  helpDeskIssues = [],
  consumableLogs = [],
  assetConsumptions = [],
  categories = [],
  subCategories = [],
  brands = []
) {
  const assetIndex = buildAssetIndex(assets);
  const stockMap = buildConsumableStockMap(consumableLogs, assetConsumptions);

  // Dynamic Lookup Maps (checks both .id and .categoryId / .name and .categoryName)
  const categoryMap = new Map(
    categories.map((c) => [c.id || c.categoryId, c.name || c.categoryName])
  );
  const subCategoryMap = new Map(
    subCategories.map((s) => [s.id || s.subCategoryId, s.name || s.subCategoryName])
  );
  const brandMap = new Map(
    brands.map((b) => [b.id || b.brandId, b.name || b.brandName])
  );

  const ticketByAsset = new Map();
  helpDeskIssues.forEach((t) => {
    if (!t) return;
    assetKeys(t).forEach((k) => {
      const prev = ticketByAsset.get(k);
      if (
        !prev ||
        new Date(t.createdAt || t._createdAtRaw || 0) >
          new Date(prev.createdAt || prev._createdAtRaw || 0)
      ) {
        ticketByAsset.set(k, t);
      }
    });
  });

  const findTicket = (row, asset) => {
    const explicit = row.ticketId || row.ticket_id || row.helpdeskTicketId;
    if (explicit) {
      const hit = helpDeskIssues.find(
        (t) => key(t.id) === key(explicit) || key(t.ticketId) === key(explicit)
      );
      if (hit) return hit;
    }
    for (const k of assetKeys(row)) {
      if (ticketByAsset.has(k)) return ticketByAsset.get(k);
    }
    if (asset) {
      for (const k of assetKeys(asset)) {
        if (ticketByAsset.has(k)) return ticketByAsset.get(k);
      }
    }
    return null;
  };

  const eventById = new Map();
  auditEvents.forEach((e) => {
    if (!e || isDeletedDoc(e)) return;
    if (e.id) eventById.set(String(e.id), e);
    if (e.audit_event_id) eventById.set(String(e.audit_event_id), e);
  });

  const historyRows = (Array.isArray(auditHistory) ? auditHistory : []).filter((h) => {
    if (!h || isDeletedDoc(h)) return false;
    const eid = historyEventId(h);
    return eid && eventById.has(eid);
  });

  const auditVerificationFromHistory = historyRows.map((h) => {
    const ev = eventById.get(historyEventId(h)) || {};
    const asset = findAsset(h, assetIndex);
    return {
      auditTitle: actualText(ev.title, ev.auditTitle, h.audit_title, h.auditTitle) || 'N/A',
      auditLocation:
        actualText(
          ev.location_name,
          ev.locationName,
          h.location_name,
          h.locationName,
          asset?.locationName
        ) || 'N/A',
      scheduledDate: toDateStr(ev.created_at || ev.createdAt || h.created_at || h.createdAt),
      auditedAtDate: toDateStr(
        h.audited_at || h.auditedAt || h.updated_at || h.updatedAt || ev.updated_at || ev.updatedAt
      ),
      auditorName:
        actualText(
          h.audited_by_name,
          h.auditedByName,
          h.updated_by_name,
          ev.auditor_name,
          ev.auditorName
        ) || 'N/A',
      assetTag:
        h.asset_tag || h.assetTag || h.asset_id || h.assetId || asset?.assetId || asset?.id || 'N/A',
      assetName: h.asset_name || h.assetName || asset?.assetName || asset?.name || 'N/A',
      category: h.category || categoryMap.get(asset?.categoryId) || 'N/A',
      brand: h.brand || brandMap.get(asset?.brandId) || 'N/A',
      auditStatus: resolveAuditStatus(h, ev),
      auditorRemarks: actualText(h.remark, h.remarks, h.notes, h.observation, h.auditorRemarks),
    };
  });

  return {
    assetRegistry: assets.map((a) => ({
      assetId: a.assetId || a.id || 'N/A',
      assetName: a.assetName || a.name || 'N/A',
      category: categoryMap.get(a.categoryId) || 'N/A',
      subCategory: subCategoryMap.get(a.subCategoryId) || 'N/A',
      brand: brandMap.get(a.brandId) || 'N/A',
      serialNumber: a.manufacturerSerialNumber || a.serialNumber || 'N/A',
      assetType: a.assetType || ASSET_TYPES?.PRIMARY || 'Primary',
      ownershipType: a.ownershipType || 'Owned',
      location: a.locationName || a.locationCode || 'N/A',
      department: a.assignedDepartmentName || 'N/A',
      assignedUser: a.assignedToName || 'Unassigned',
      status: a.status || ASSET_STATUS?.AVAILABLE || 'Available',
      purchaseDate: toDateStr(a.purchaseDate),
      purchaseValue: num(a.purchaseValue ?? a.cost),
      warrantyExpiry: toDateStr(a.warrantyExpiry),
      depreciationMethod: a.depreciationMethod || 'N/A',
      usefulLifeYears: a.usefulLife || 0,
      depreciationRate: a.depreciationRate || 0,
      residualValue: a.residualValue || 0,
    })),

    depreciationRegister: assets
      .filter((a) => a && a.ownershipType !== 'Leased' && a.ownershipType !== 'Rented')
      .map((a) => ({
        assetId: a.assetId || a.id || 'N/A',
        assetName: a.assetName || a.name || 'N/A',
        category: categoryMap.get(a.categoryId) || 'N/A',
        purchaseValue: num(a.purchaseValue ?? a.cost),
        depreciationMethod: a.depreciationMethod || 'N/A',
        usefulLifeYears: a.usefulLife || 0,
        depreciationRate: a.depreciationRate || 0,
        residualValue: a.residualValue || 0,
        accumulatedDepreciation: a.accumulatedDepreciation || 0,
        currentNetBookValue:
          a.currentNetBookValue || a.netBookValue || num(a.purchaseValue ?? a.cost),
      })),

    expiringAssets: assets
      .map((a) => {
        const expiry = toDate(a.warrantyExpiry || a.leaseEndDate);
        return { ...a, _expiry: expiry, _days: daysUntil(expiry) };
      })
      .filter((a) => a._days != null && a._days > 0 && a._days <= 90)
      .map((a) => ({
        assetId: a.assetId || a.id || 'N/A',
        assetName: a.assetName || 'N/A',
        category: categoryMap.get(a.categoryId) || 'N/A',
        location: a.locationName || 'N/A',
        department: a.assignedDepartmentName || 'N/A',
        ownershipType: a.ownershipType || 'Owned',
        vendorName: a.vendorName || a.leaseVendorName || 'N/A',
        coverageType: a.ownershipType === 'Leased' ? 'Lease' : 'Warranty',
        expiryDate: toDateStr(a._expiry),
        daysRemaining: a._days,
      })),

    expiredAssets: assets
      .map((a) => {
        const expiry = toDate(a.warrantyExpiry || a.leaseEndDate);
        return { ...a, _expiry: expiry, _days: daysUntil(expiry) };
      })
      .filter((a) => a._days != null && a._days <= 0 && a.status !== ASSET_STATUS?.DISPOSED)
      .map((a) => ({
        assetId: a.assetId || a.id || 'N/A',
        assetName: a.assetName || 'N/A',
        category: categoryMap.get(a.categoryId) || 'N/A',
        location: a.locationName || 'N/A',
        department: a.assignedDepartmentName || 'N/A',
        assignedCustodian: a.assignedToName || 'Unassigned',
        coverageType: a.ownershipType === 'Leased' ? 'Lease' : 'Warranty',
        expiryDate: toDateStr(a._expiry),
        daysLapsed: Math.abs(a._days),
        renewalStatus: a.renewalStatus || 'Pending',
      })),

    missingAssets: (() => {
      const fromHistory = historyRows
        .filter((h) => key(h.status) === 'missing' || key(h.audit_status) === 'missing')
        .map((h) => {
          const asset = findAsset(h, assetIndex);
          const ev = eventById.get(historyEventId(h)) || {};
          const ticket = findTicket(h, asset);
          return {
            assetId:
              h.asset_tag || h.asset_id || h.assetId || asset?.assetId || asset?.id || 'N/A',
            assetName: h.asset_name || h.assetName || asset?.assetName || 'N/A',
            category: h.category || categoryMap.get(asset?.categoryId) || 'N/A',
            brand: h.brand || brandMap.get(asset?.brandId) || 'N/A',
            assignedCustodian: asset?.assignedToName || 'Unassigned',
            department: asset?.assignedDepartmentName || 'N/A',
            lastKnownLocation:
              asset?.locationName || ev.location_name || ev.locationName || h.location_name || 'N/A',
            lastVerificationDate: toDateStr(h.audited_at || h.updated_at || h.updatedAt),
            reportedByAuditor:
              h.audited_by_name || h.updated_by_name || ev.auditor_name || ev.auditorName || 'N/A',
            helpdeskTicketId: ticket?.id || ticket?.ticketId || h.ticketId || 'N/A',
            ticketStatus: ticket?.status || h.ticketStatus || 'N/A',
            ticketPriority: ticket?.priority || 'High',
            auditObservationDetails: actualText(h.remark, h.remarks, h.notes, h.observation),
          };
        });

      if (fromHistory.length) return fromHistory;

      return assets
        .filter((a) => {
          const s = key(a.status);
          const as = key(a.auditStatus);
          return s === 'missing' || s === 'lost' || as === 'missing' || as === 'lost';
        })
        .map((a) => {
          const ticket = findTicket(a, a);
          return {
            assetId: a.assetId || a.id || 'N/A',
            assetName: a.assetName || 'N/A',
            category: categoryMap.get(a.categoryId) || 'N/A',
            brand: brandMap.get(a.brandId) || 'N/A',
            assignedCustodian: a.assignedToName || 'Unassigned',
            department: a.assignedDepartmentName || 'N/A',
            lastKnownLocation: a.locationName || 'N/A',
            lastVerificationDate: toDateStr(a.lastAuditDate || a.updatedAt),
            reportedByAuditor: a.updatedBy || a.auditorName || 'N/A',
            helpdeskTicketId: ticket?.id || a.ticketId || 'N/A',
            ticketStatus: ticket?.status || a.ticketStatus || 'N/A',
            ticketPriority: ticket?.priority || 'High',
            auditObservationDetails: actualText(a.auditRemarks, a.remark, a.remarks),
          };
        });
    })(),

    assetAuditVerification:
      auditVerificationFromHistory.length > 0
        ? auditVerificationFromHistory
        : auditEvents.map((a) => ({
            auditTitle: actualText(a.title, a.auditTitle) || a.id || 'N/A',
            auditLocation: actualText(a.location_name, a.locationName) || 'N/A',
            scheduledDate: toDateStr(a.created_at || a.createdAt),
            auditedAtDate: toDateStr(a.updated_at || a.updatedAt),
            auditorName: actualText(a.auditor_name, a.auditorName) || 'N/A',
            assetTag: 'Multiple',
            assetName: 'Audit Event Summary',
            category: 'N/A',
            brand: 'N/A',
            auditStatus: resolveAuditStatus({}, a),
            auditorRemarks: actualText(a.remarks, a.remark, a.notes, a.description),
          })),

    unverifiedAssets: historyRows
      .filter((h) => {
        const s = key(resolveAuditStatus(h));
        if (!['pending', 'unverified', 'unaudited'].includes(s)) return false;
        const ev = eventById.get(historyEventId(h));
        if (!ev) return false;
        return key(ev.status) !== 'completed';
      })
      .map((h) => {
        const ev = eventById.get(historyEventId(h)) || {};
        const asset = findAsset(h, assetIndex);
        return {
          auditTitle: actualText(ev.title, ev.auditTitle) || 'Pending Verification',
          assetTag: h.asset_tag || h.asset_id || h.assetId || 'N/A',
          assetName: h.asset_name || h.assetName || 'N/A',
          category: h.category || categoryMap.get(asset?.categoryId) || 'N/A',
          brand: h.brand || brandMap.get(asset?.brandId) || 'N/A',
          currentLocation: ev.location_name || ev.locationName || h.location_id || 'N/A',
          department: asset?.assignedDepartmentName || 'N/A',
          assignedAuditor:
            actualText(ev.auditor_name, ev.auditorName, h.updated_by_name) ||
            'Pending Assignment',
          auditStatus: resolveAuditStatus(h, ev),
        };
      }),

    assetMovement: (() => {
      const movements = [];
      assets.forEach((a) => {
        if (!a) return;
        const assetId = a.assetId || a.id || 'N/A';
        const assetName = a.assetName || a.name || 'N/A';
        const locHist = Array.isArray(a.locationHistory)
          ? a.locationHistory
          : Array.isArray(a.movementHistory)
          ? a.movementHistory
          : Array.isArray(a.transferHistory)
          ? a.transferHistory
          : [];

        locHist.forEach((m) => {
          if (!m) return;
          const dateVal = m.changedAt || m.date || m.timestamp || m.movedAt || m.createdAt;
          movements.push({
            assetId,
            assetName,
            movementDate: toDateStr(dateVal),
            initiatedByUser:
              actualText(m.changedBy, m.initiatedBy, m.movedBy, m.updatedBy) || 'N/A',
            previousUser: 'N/A',
            newUser: 'N/A',
            previousDepartment: 'N/A',
            newDepartment: 'N/A',
            previousLocation:
              actualText(m.from, m.fromLocation, m.previousLocation) || 'N/A',
            newLocation:
              actualText(m.to, m.toLocation, m.newLocation, m.locationName) || 'N/A',
            reasonForShift:
              actualText(m.reason, m.notes, m.type, m.remarks) || 'Location Transfer',
            _sortDate: toDate(dateVal) || new Date(0),
          });
        });

        const asgHist = Array.isArray(a.assignmentHistory) ? a.assignmentHistory : [];
        asgHist.forEach((m) => {
          if (!m) return;
          const dateVal = m.assignedOn || m.date || m.changedAt || m.createdAt;
          movements.push({
            assetId,
            assetName,
            movementDate: toDateStr(dateVal),
            initiatedByUser:
              actualText(m.assignedByName, m.assignedBy, m.changedBy) || 'N/A',
            previousUser:
              actualText(
                m.releasedFromUserName,
                m.previousAssignedToName,
                m.previousUser
              ) || 'N/A',
            newUser: actualText(m.assignedToName, m.newUser) || 'Unassigned',
            previousDepartment: actualText(m.previousDepartment) || 'N/A',
            newDepartment:
              actualText(m.assignedDepartmentName, m.newDepartment, m.department) || 'N/A',
            previousLocation:
              actualText(m.previousLocation, a.currentLocationName) || 'N/A',
            newLocation: actualText(m.newLocation, a.locationName) || 'N/A',
            reasonForShift:
              actualText(m.notes, m.reason, m.type, m.remarks) || 'Assignment Change',
            _sortDate: toDate(dateVal) || new Date(0),
          });
        });
      });
      movements.sort((a, b) => b._sortDate - a._sortDate);
      return movements.map(({ _sortDate, ...rest }) => rest);
    })(),

    consumableStock: assets.filter(isConsumable).map((a) => {
      const ids = [a.id, a.assetId].filter(Boolean).map(String);
      let purchased = 0,
        issued = 0,
        consumedExtra = 0;
      ids.forEach((id) => {
        const row = stockMap.get(id);
        if (!row) return;
        purchased += row.purchased;
        issued += row.issued;
        consumedExtra += row.consumed;
      });
      const openingStock = num(a.openingStock);
      const available =
        a.quantity != null
          ? num(a.quantity)
          : Math.max(openingStock + purchased - issued - consumedExtra, 0);
      return {
        assetId: a.assetId || a.id || 'N/A',
        assetName: a.assetName || 'N/A',
        category: categoryMap.get(a.categoryId) || 'N/A',
        brand: brandMap.get(a.brandId) || 'N/A',
        location: a.locationName || 'N/A',
        openingStock,
        purchasedQuantity: purchased,
        consumedQuantity: issued + consumedExtra,
        currentAvailableStock: available,
      };
    }),
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Safe fetchers
// ─────────────────────────────────────────────────────────────────────────────

function asArray(value) {
  if (Array.isArray(value)) return value;
  if (value && Array.isArray(value.data)) return value.data;
  if (value && Array.isArray(value.items)) return value.items;
  if (value && Array.isArray(value.results)) return value.results;
  return [];
}

async function safeFetch(label, fn) {
  try {
    const result = await fn();
    return asArray(result);
  } catch (err) {
    console.warn(`[useAdminReports] ${label} fetch failed:`, err?.message || err);
    return [];
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────────────────────────────────────

export const useAdminReports = (currentUser) => {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const fetchCounter = useRef(0);
  const userId = resolveUserId(currentUser);

  const fetchReport = useCallback(
    async (isSilentBackgroundSync = false) => {
      if (!userId) {
        console.warn('[useAdminReports] No userId — skipping fetch');
        setError(currentUser ? "User profile missing identifier" : "User session not found");
        setLoading(false);
        setIsRefreshing(false);
        return;
      }

      const currentFetchId = ++fetchCounter.current;
      let showSkeleton = !isSilentBackgroundSync;

      try {
        // ── 1. Resolve user profile ────────────────────────────────────────
        let userProfile = null;
        try {
          if (typeof userService?.fetchUserById === 'function') {
            userProfile = await userService.fetchUserById(userId);
          }
        } catch (e) {
          console.warn('[useAdminReports] userService lookup failed:', e?.message);
        }

        if (!userProfile) {
          userProfile = currentUser || {};
        }

        let localUserData = {};
        try {
          localUserData = JSON.parse(localStorage.getItem('userData') || '{}');
        } catch (e) {}

        const companyId =
          userProfile.companyId ||
          currentUser?.companyId ||
          localUserData.companyId;

        if (!companyId) {
          throw new Error('Company ID not found in user profile');
        }

        const sessionUser = {
          ...userProfile,
          uid: resolveUserId(userProfile) || userId,
          userId: userProfile.userId || userId,
          role: userProfile.role || currentUser?.role || 'admin',
          companyId,
          email: userProfile.email || currentUser?.email || '',
          locationCode: userProfile.locationCode || currentUser?.locationCode || '',
        };

        const cachedEntry = sessionReportCache.get(userId);
        if (cachedEntry && cachedEntry.data?.companyId !== companyId) {
          sessionReportCache.delete(userId);
          setReportData(null);
          showSkeleton = true;
        }

        if (showSkeleton) {
          setLoading(true);
          setIsRefreshing(false);
        } else {
          setIsRefreshing(true);
        }

        // ── 2. Fetch all data sources AND Master Data using individual services ──
        const [
          assets, 
          auditEvents, 
          helpDeskIssues, 
          consumableLogs, 
          assetConsumptions,
          categories,
          subCategories,
          brands
        ] = await Promise.all([
          safeFetch('assets', () =>
            assetService?.fetchAssets
              ? assetService.fetchAssets({ companyId, locationCode: 'ALL' })
              : Promise.resolve([])
          ),
          safeFetch('auditEvents', () =>
            typeof fetchAuditEvents === 'function'
              ? fetchAuditEvents(companyId)
              : Promise.resolve([])
          ),
          safeFetch('helpDeskIssues', () =>
            helpDeskService?.getTicketsForRole
              ? helpDeskService.getTicketsForRole('admin', { companyId })
              : Promise.resolve([])
          ),
          safeFetch('consumableLogs', () =>
            consumableSpareLogService?.getAllLogs
              ? consumableSpareLogService.getAllLogs(sessionUser, {}, 5000)
              : Promise.resolve([])
          ),
          safeFetch('assetConsumptions', () =>
            consumableSpareLogService?.getConsumptionHistory
              ? consumableSpareLogService.getConsumptionHistory(sessionUser, {}, 5000)
              : Promise.resolve([])
          ),
          // Fetch Master Data using individual services (defensive method resolution)
          safeFetch('categories', () => {
            if (typeof categoryService?.fetchCategories === 'function') {
              return categoryService.fetchCategories({ companyId });
            }
            if (typeof categoryService?.getCategories === 'function') {
              return categoryService.getCategories(companyId);
            }
            return Promise.resolve([]);
          }),
          safeFetch('subCategories', () => {
            if (typeof subCategoryService?.fetchSubCategories === 'function') {
              return subCategoryService.fetchSubCategories({ companyId });
            }
            if (typeof subCategoryService?.getSubCategories === 'function') {
              return subCategoryService.getSubCategories(companyId);
            }
            return Promise.resolve([]);
          }),
          safeFetch('brands', () => {
            if (typeof brandService?.fetchBrands === 'function') {
              return brandService.fetchBrands({ companyId });
            }
            if (typeof brandService?.getBrands === 'function') {
              return brandService.getBrands(companyId);
            }
            return Promise.resolve([]);
          }),
        ]);

        // ── 3. Load audit history (per-event) ──────────────────────────────
        let auditHistory = [];
        if (auditEvents.length > 0 && typeof fetchAuditEventAssets === 'function') {
          const historyResults = await Promise.all(
            auditEvents.map((ev) =>
              safeFetch(`auditEventAssets:${ev.id}`, () =>
                fetchAuditEventAssets(ev.id, companyId)
              )
            )
          );
          auditHistory = historyResults.flat();
        }

        // ── 4. Build report payload ────────────────────────────────────────
        const detailedReports = buildDetailedReports(
          assets,
          auditEvents,
          auditHistory,
          helpDeskIssues,
          consumableLogs,
          assetConsumptions,
          categories,
          subCategories,
          brands
        );

        const structuredPayload = {
          company:
            userProfile.company ||
            userProfile.companyName ||
            'Corporate Workspace',
          contactPerson:
            userProfile.displayName || userProfile.name || '—',
          companyId,
          createdAt: new Date(),
          detailedReports,
        };

        if (currentFetchId !== fetchCounter.current) return;

        sessionReportCache.set(userId, { data: structuredPayload, timestamp: Date.now() });
        setReportData(structuredPayload);
        setError(null);
      } catch (err) {
        if (currentFetchId !== fetchCounter.current) return;
        console.error('[useAdminReports] fetch error:', err);
        setError(`Failed to load report: ${err.message || 'Unknown error'}`);
        setReportData(null);
      } finally {
        if (currentFetchId !== fetchCounter.current) return;
        setLoading(false);
        setIsRefreshing(false);
      }
    },
    [userId, currentUser]
  );

  useEffect(() => {
    if (!userId) {
      if (currentUser) {
        setError("User account identifier missing.");
      } else {
        setError("User session not found.");
      }
      setReportData(null);
      setLoading(false);
      setIsRefreshing(false);
      return;
    }

    if (sessionReportCache.has(userId)) {
      setReportData(sessionReportCache.get(userId).data);
      setLoading(false);
      setError(null);
      fetchReport(true);
    } else {
      setReportData(null);
      fetchReport(false);
    }
  }, [userId, fetchReport]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && userId) fetchReport(true);
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [userId, fetchReport]);

  return {
    reportData,
    loading,
    isRefreshing,
    error,
    refresh: () => fetchReport(true),
  };
};